import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { themes } from "services/constants";
import { showToast } from "services/utils/Status";
import { Info, Edit, Delete } from "@mui/icons-material";
import { Loader } from "components/Loader";
import { useForm } from "react-hook-form";
import { leavePeriodHrms } from "services/constants";
import * as Mui from "@mui/material";
import { GlobalDialogBox } from "components/GlobalDialogBox";
import { CustomButton } from "components/CustomComponents/CustomButton";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { tableRowSx, headerMessage } from "services/constants";
import { tableContainerSx, tableHeaderSx } from "services/constants";
import { useFetch } from "services/hooks/useFetch";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { customAPI } from "services/utils/CustomAPI";
import sessionHandling from "services/utils/notificationUtils";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { tooltipStyleProps } from "services/constants/Styles";
export const LeaveYear = () => {
  const { domain, token, globalSearchValue, userScreenAccess } = useSelector(
    (state) => state.tokenReducer
  );
  const dmy = (value) => {
    const date = new Date(value);
    return dayjs(date).format(`DD/MM/YYYY`);
  };
  const ymd = (value) => {
    const date = new Date(value);
    return dayjs(date).format(`YYYY-MM-DD`);
  };
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [openDialog, setOpenDialog] = useState(false);
  const {
    result: masterLeaveYear,
    loading: loader,
    setLoad: setFetchedData,
  } = useFetch(`${domain}leaves-years/`);
  const [leaveYear, setLeaveYear] = useState(masterLeaveYear);
  const [popupTitle, setPopupTitle] = useState("");
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState("");
  const [leaveCreditDay, setLeaveCreditDay] = useState("");
  const [buttonLoader, setButtonLoader] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (startDate) {
      const start = dayjs(startDate);
      const end = start?.add(1, "year").subtract(1, "day");
      const day = start?.date();
      setLeaveCreditDay(day);
      setEndDate(end);
    }
  }, [startDate]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [page]);

  const handleStartDate = (value) => {
    if (value) {
      setStartDate(dayjs(value));
      setTimeout(() => null, 2000);
    }
  };
  const {
    register,
    reset,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({ mode: "onBlur" });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClose = () => {
    setPopupTitle("");
    setOpenDialog(false);
    setStartDate(dayjs());
    reset();
  };

  const checkSpecialChar = (e) => {
    if (!/^[0-9a-zA-Z\- ]$/.test(e.key)) {
      e.preventDefault();
    }
  };

  useEffect(() => {
    const filteredResults = masterLeaveYear?.filter((item) =>
      leavePeriodHrms.some((key) =>
        item[key]
          ?.toString()
          .toLowerCase()
          .includes(globalSearchValue.toString().toLowerCase())
      )
    );
    setLeaveYear(filteredResults);
    setPage(globalSearchValue ? 0 : page);
  }, [globalSearchValue, masterLeaveYear, page]);

  const addButtonAction = () => {
    setStartDate(dayjs());
    setPopupTitle("Add Leave Year");
    setOpenDialog(true);
    reset();
  };

  const onSubmit = async (data) => {
    setButtonLoader(true);
    setTimeout(() => null, 1000);
    if (data) {
      const id = getValues("id");
      const url = id ? `leaves-years/${id}/` : `leaves-years/`;
      const method = id ? "PUT" : "POST";
      const start = ymd(startDate);
      const end = ymd(endDate);
      const response = await customAPI(`${domain}${url}`, method, {
        name: data?.name.trim(),
        period_start: start,
        period_end: end,
        leave_crediting_day: leaveCreditDay,
      });
      if (response.ok) {
        setFetchedData(true);
        setOpenDialog(false);
        const message = id ? `updated` : `added`;
        showToast("success", `Leave year ${message}.`);
      }
    }
    setButtonLoader(false);
  };

  const deleteLeaveYear = async (id) => {
    setButtonLoader(true);
    setTimeout(() => null, 1000);
    try {
      const response = await fetch(`${domain}leaves-years/${id}/`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `token ${token}`,
        },
      });
      const res = response.json();
      if (response.ok) {
        showToast("success", res.message);
        setFetchedData(true);
      } else if (response.status === 409) {
        sessionHandling();
      } else {
        throw new Error(
          "Cannot delete this leave year during its active period."
        );
      }
    } catch (error) {
      console.error('[Leaveyear] operation failed:', error);
      showToast("error", "Couldn't save the leave settings. Please try again.");
    } finally {
      setButtonLoader(false);
    }
  };

  return loader ? (
    <Loader info="Loading..." />
  ) : (
    <React.Fragment>
      {headerMessage(
        "Leave Year",
        "Define the leave year periods used to calculate leave balances",
      )}
      <Mui.Grid
        container
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-end",
          alignItems: "flex-end",
          paddingTop: 2,
          paddingRight: 2.5,
        }}
      >
        <CustomButton
          actionFuntion={() => {
            if (
              checkEditorPermission(
                userScreenAccess?.module_details,
                "LMS",
                "Leave Year"
              )
            ) {
              addButtonAction();
            } else {
              showToast(
                "error",
                "Access denied: You are not authorized to add leave year"
              );
            }
          }}
          actionName="Add Leave Year"
          typeName="button"
        />
      </Mui.Grid>
      <Mui.Grid container sx={{ mt: 1 }}>
        <Mui.TableContainer ref={containerRef} sx={tableContainerSx}>
          <Mui.Table className="border">
            <Mui.TableHead sx={tableHeaderSx}>
              <Mui.TableRow sx={tableRowSx}>
                <Mui.TableCell
                  sx={{ color: themes.headLine, fontWeight: "bold" }}
                >
                  Leave Year Name
                </Mui.TableCell>
                <Mui.TableCell
                  sx={{ color: themes.headLine, fontWeight: "bold" }}
                >
                  Period Start
                </Mui.TableCell>
                <Mui.TableCell
                  sx={{ color: themes.headLine, fontWeight: "bold" }}
                >
                  Period End
                </Mui.TableCell>
                <Mui.TableCell
                  sx={{ color: themes.headLine, fontWeight: "bold" }}
                >
                  Leave Crediting Day
                </Mui.TableCell>
                <Mui.TableCell
                  sx={{ color: themes.headLine, fontWeight: "bold" }}
                >
                  Actions
                </Mui.TableCell>
              </Mui.TableRow>
            </Mui.TableHead>
            <Mui.TableBody>
              {leaveYear?.length === 0 && !loader ? (
                <TableEmptyState dataLength={masterLeaveYear} />
              ) : (
                leaveYear
                  ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  ?.map((item, index) => (
                    <Mui.TableRow sx={tableRowSx}>
                      <Mui.TableCell
                        sx={{ color: themes.headLine, fontWeight: "bold" }}
                      >
                        <Mui.Typography>
                          {item?.name ? item?.name : "N/A"}
                        </Mui.Typography>
                      </Mui.TableCell>
                      <Mui.TableCell
                        sx={{ color: themes.headLine, fontWeight: "bold" }}
                      >
                        <Mui.Typography>
                          {item?.period_start ? dmy(item?.period_start) : "N/A"}
                        </Mui.Typography>
                      </Mui.TableCell>
                      <Mui.TableCell
                        sx={{ color: themes.headLine, fontWeight: "bold" }}
                      >
                        <Mui.Typography>
                          {item?.period_end ? dmy(item?.period_end) : "N/A"}
                        </Mui.Typography>
                      </Mui.TableCell>
                      <Mui.TableCell
                        sx={{ color: themes.headLine, fontWeight: "bold" }}
                      >
                        <Mui.Typography>
                          {item?.leave_crediting_day
                            ? item?.leave_crediting_day
                            : "N/A"}
                        </Mui.Typography>
                      </Mui.TableCell>
                      <Mui.TableCell
                        sx={{ color: themes.headLine, fontWeight: "bold" }}
                      >
                        <Mui.IconButton
                          onClick={() => {
                            if (
                              checkEditorPermission(
                                userScreenAccess?.module_details,
                                "LMS",
                                "Leave Year"
                              )
                            ) {
                              setStartDate(dayjs(item?.period_start));
                              setValue("name", item.name);
                              setValue("id", item.id);
                              setPopupTitle("Edit Leave Year");
                              setOpenDialog(true);
                            } else {
                              showToast(
                                "error",
                                "Access denied: You are not authorized to edit leave year"
                              );
                            }
                          }}
                        >
                          <Edit />
                        </Mui.IconButton>
                        <Mui.IconButton
                          onClick={() => {
                            if (
                              checkEditorPermission(
                                userScreenAccess?.module_details,
                                "LMS",
                                "Leave Year"
                              )
                            ) {
                              setStartDate(dayjs(item?.period_start));
                              setValue("name", item.name);
                              setValue("id", item.id);
                              setPopupTitle("Delete Leave Year");
                              setOpenDialog(true);
                            } else {
                              showToast(
                                "error",
                                "Access denied: You are not authorized to delete leave year"
                              );
                            }
                          }}
                        >
                          <Delete />
                        </Mui.IconButton>
                      </Mui.TableCell>
                    </Mui.TableRow>
                  ))
              )}
            </Mui.TableBody>
          </Mui.Table>
        </Mui.TableContainer>
      </Mui.Grid>
      <Mui.Grid
        container
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-end",
          alignItems: "flex-end",
        }}
      >
        {leaveYear?.length > 25 ? (
          <Mui.TablePagination
            className="custom-pagination"
            component="div"
            rowsPerPageOptions={[25, 50, 75, 100]}
            count={leaveYear?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        ) : null}
      </Mui.Grid>
      <GlobalDialogBox
        key={popupTitle}
        handleCloseDialog={handleClose}
        open={openDialog}
        title={popupTitle}
        minWidth={650}
      >
        {popupTitle === "Delete Leave Year" ? (
          <Mui.Grid container>
            <Mui.Grid xs={12}>
              <Mui.Typography sx={{ fontSize: 22, paddingBottom: 2 }}>
                Are you sure you want to delete{" "}
                <b style={{ color: themes.redColor }}>{getValues("name")}</b>?
              </Mui.Typography>
            </Mui.Grid>
            <Mui.Grid container>
              <Mui.Grid
                container
                sx={{
                  paddingTop: 1,
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "flex-center",
                }}
              >
                <CustomButton
                  actionFuntion={() => {
                    setOpenDialog(false);
                    handleClose();
                  }}
                  actionName="No"
                  typeName="submit"
                />
                &nbsp;&nbsp;
                <CustomButton
                  actionFuntion={() => {
                    setOpenDialog(false);
                    deleteLeaveYear(getValues("id"));
                  }}
                  disableAction={
                    openDialog && buttonLoader ? buttonLoader : false
                  }
                  actionName="Yes"
                  typeName="submit"
                />
              </Mui.Grid>
            </Mui.Grid>
          </Mui.Grid>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <Mui.Grid container sx={{ paddingBottom: 1 }} spacing={2}>
              <Mui.Grid item xs={4} sx={{ justifyContent: "flex-end" }}>
                <Mui.Stack
                  direction="Row"
                  sx={{
                    paddingTop: 1,
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "end",
                    alignItems: "flex-end",
                    paddingRight: 1,
                  }}
                >
                  <Mui.Typography>Leave Year Name</Mui.Typography> &nbsp;&nbsp;
                  <Mui.Tooltip
                    arrow
                    slotProps={tooltipStyleProps}
                    title="The leave year defines when the leave cycle starts and ends for an year, renewing the leaves allowed per year and triggers the carryovers."
                  >
                    <Info />
                  </Mui.Tooltip>
                </Mui.Stack>
              </Mui.Grid>
              <Mui.Grid item xs={8}>
                <Mui.TextField
                  placeholder="Enter the leave year name"
                  {...register("name", {
                    required: "Leave year name is required",
                    minLength: {
                      value: 3,
                      message: "Minimum 3 characters are required",
                    },
                    maxLength: {
                      value: 100,
                      message: "Maximum 100 characters are allowed",
                    },
                  })}
                  size="small"
                  InputProps={{
                    inputProps: { maxLength: 100 },
                  }}
                  error={Boolean(errors.name)}
                  helperText={errors.name?.message || ""}
                  onKeyPress={checkSpecialChar}
                  fullWidth
                />
              </Mui.Grid>
            </Mui.Grid>
            <Mui.Grid container sx={{ paddingBottom: 1 }} spacing={2}>
              <Mui.Grid item xs={4} sx={{ justifyContent: "flex-end" }}>
                <Mui.Stack
                  direction="Row"
                  sx={{
                    paddingTop: 1,
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "end",
                    alignItems: "flex-end",
                    paddingRight: 1,
                  }}
                >
                  <Mui.Typography>Period Start</Mui.Typography> &nbsp;&nbsp;
                  <Mui.Tooltip
                    arrow
                    slotProps={tooltipStyleProps}
                    title="Period start helps to select the start date of one leave cycle."
                  >
                    <Info />
                  </Mui.Tooltip>
                </Mui.Stack>
              </Mui.Grid>
              <Mui.Grid item xs={8}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer
                    components={["DatePicker"]}
                    style={{ width: "100%" }}
                  >
                    <DatePicker
                      size="small"
                      slotProps={{ textField: { size: "small" } }}
                      value={startDate ? dayjs(startDate) : dayjs()}
                      format="DD/MM/YYYY"
                      onChange={handleStartDate}
                      sx={{ width: "100%" }}
                      renderInput={(params) => (
                        <Mui.TextField {...params} size="small" fullWidth />
                      )}
                    />
                  </DemoContainer>
                </LocalizationProvider>
              </Mui.Grid>
            </Mui.Grid>
            <Mui.Grid container spacing={2}>
              <Mui.Grid item xs={4} sx={{ justifyContent: "flex-end" }}>
                <Mui.Stack
                  direction="Row"
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "end",
                    alignItems: "flex-end",
                    paddingRight: 1,
                  }}
                >
                  <Mui.Typography>Period End</Mui.Typography> &nbsp;&nbsp;
                  <Mui.Tooltip
                    arrow
                    slotProps={tooltipStyleProps}
                    title="Period end helps to select the end date of one leave cycle."
                  >
                    <Info />
                  </Mui.Tooltip>
                </Mui.Stack>
              </Mui.Grid>
              <Mui.Grid item xs={8}>
                <Mui.Typography>&nbsp;{dmy(endDate)}</Mui.Typography>{" "}
                &nbsp;&nbsp;
              </Mui.Grid>
            </Mui.Grid>

            <Mui.Grid container spacing={2}>
              <Mui.Grid item xs={4} sx={{ justifyContent: "flex-end" }}>
                <Mui.Stack
                  direction="Row"
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "end",
                    alignItems: "flex-end",
                    paddingRight: 1,
                  }}
                >
                  <Mui.Typography>Leave Crediting Day</Mui.Typography>{" "}
                  &nbsp;&nbsp;
                  <Mui.Tooltip
                    arrow
                    slotProps={tooltipStyleProps}
                    title="Leave crediting day helps to select the end date of one leave cycle."
                  >
                    <Info />
                  </Mui.Tooltip>
                </Mui.Stack>
              </Mui.Grid>
              <Mui.Grid item xs={8}>
                <Mui.Typography>
                  &nbsp;{leaveCreditDay ? leaveCreditDay : 0}
                </Mui.Typography>{" "}
                &nbsp;&nbsp;
              </Mui.Grid>
            </Mui.Grid>
            <Mui.Stack
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "flex-end",
                alignItems: "flex-end",
              }}
            >
              <CustomButton
                actionFuntion={() => {
                  setOpenDialog(false);
                  handleClose();
                }}
                actionName="Cancel"
                typeName="button"
              />
              &nbsp;&nbsp;
              <CustomButton
                actionFuntion={() => {
                  handleSubmit(onSubmit);
                }}
                actionName={popupTitle === "Add Leave Year" ? "Add" : "Update"}
                typeName="submit"
                disableAction={
                  openDialog && buttonLoader ? buttonLoader : false
                }
              />
            </Mui.Stack>
          </form>
        )}
      </GlobalDialogBox>
    </React.Fragment>
  );
};
